import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
import { Link } from "react-router-dom";

function IslamicTradePage18() {
  const contentRef = useRef(null);
  const links = {
    prev: "/islamic-trade-page-17", 
    index: "/index",                 
    next: "/islamic-trade-page-19", 
  };
  const titleText = "Principles of Partnership (Sharikah)";
  const questions = [
    {
      text: "1. In an Islamic partnership (Sharikah), how must losses be distributed among the partners?",
      options: [
        {
          value: "A",
          label: "A.  According to the agreed profit ratio.",
        },
        {
          value: "B",
          label: "B. Equally, regardless of capital contribution.",
        },
        {
          value: "C",
          label: "C. According to the capital contribution of each partner.",
        },
        {
          value: "D",
          label: "D.  Based on the time invested by each partner.",
        },
      ],
      correct: "C",
    },
    {
      text: "2. Which of the following is not a valid form of Islamic partnership?",
      options: [
        { value: "A", label: "A. Mudarabah (Investment Partnership)" },
        { value: "B", label: "B. Musaqarah (Mortgage Partnership) )" },
        { value: "C", label: "C. Sharikat-ul-Inan (Contribution Partnership)" },
        { value: "D", label: "D. Muzara’ah (Agricultural Partnership)" },
      ],
      correct: "B",
    },
    {
      text: "3. In a Mudarabah contract, who bears the financial loss if the business fails without negligence?",
      options: [
        { value: "A", label: "A. The managing partner (Mudarib)" },
        { value: "B", label: "B. Both parties equally" },
        { value: "C", label: "C. The investor (capital provider) " },
        { value: "D", label: "D. The Islamic court" },
      ],
      correct: "C",
    },
    {
      text: "4. Which of the following conditions is impermissible in profit-sharing agreements in Sharikah?",
      options: [
        { value: "A", label: "A. Agreeing on a percentage share of profits" },
        {
          value: "B",
          label: "B. Sharing profits based on months or specific products ",
        },
        { value: "C", label: "C. Sharing profits based on mutual agreement " },
        {
          value: "D",
          label:
            "D. Having one partner manage while the other provides capital",
        },
      ],
      correct: "B",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={18}
        timerMinutes={60}
        currentLevel={1}
      />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 18</b>! Today, we explore one of the most powerful and
        collaborative concepts in Islamic finance: Sharikah, which is the
        Islamic model of partnership. Rooted in mutual trust, shared risk, and
        ethical cooperation, partnerships in Islam offer a framework for
        building prosperous ventures while staying true to Shariah principles.
        Whether it’s contributing capital, offering skills, or working the land,
        Islamic partnerships ensure that profit is earned justly and losses are
        shared fairly. In this session, we’ll uncover the types of partnerships
        recognized in Islamic law, the conditions that make them valid, and how
        they promote fairness, transparency, and unity in business dealings. In
        Islamic finance, "Sharikah" refers to a partnership where two or more
        individuals come together to share resources, efforts, and profits in a
        manner compliant with Shariah. The concept embodies mutual cooperation,
        shared responsibility, and fairness, aligning with the Islamic ethos of
        community and justice. It is a cornerstone of Islamic economic
        principles, emphasizing shared risk and equitable distribution of
        wealth.
        <br />
        <br />
        <b>Ruling from the Quran and Sunnah</b> <br />
        <br />
        The Quran encourages cooperation and mutual assistance in righteous and
        beneficial endeavors.
        <br /> Allah says: “Cooperate with one another in goodness and
        righteousness, and do not cooperate in sin and transgression. ”{" "}
        <i>
          <a href="https://quran.com/5/2"> (Surah Al-Ma’idah 5:2)</a>
        </i>
        <br />
        The Sunnah also supports partnerships as a lawful means of conducting
        business.
        <br /> The Prophet Muhammad (peace be upon him) said: Allah, Most High,
        says: "I make a third with two partners as long as one of them does not
        cheat the other, but when he cheats him, I depart from them."
        <i>
          <a href="https://sunnah.com/abudawud:3383" target="blank">
            Sunan Abu Dawood, 3383
          </a>
        </i>
        <br />
        <br />
        The Principle of Islamic Partnership Sharikah, in essence, represents a
        collaborative and mutually beneficial relationship between partners who
        agree to pool their resources and efforts for a common goal. It is
        rooted in the Islamic values of trust, transparency, and accountability,
        ensuring that all parties involved share risks and rewards equitably.
        The principle of Sharikah prohibits exploitation and ensures fairness.
        Unlike conventional partnerships, Sharikah does not permit the
        accumulation of wealth through interest (riba) or unethical means.
        Instead, it relies on profit-sharing models and joint capital
        contribution. By ensuring equitable profit
        distribution and risk-sharing, it reduces economic disparity and
        cultivates trust among partners. Sharikah also aligns economic
        activities with ethical and moral standards, promoting sustainability
        and social welfare. This principle remains a viable model in modern
        Islamic finance, encouraging partnerships that adhere to Islamic values
        while driving innovation and prosperity.
        <br />
        Partnership in Islamic finance extends beyond tangible assets to include
        shared benefits derived from items. For example, partnership can occur
        in tangible objects, such as wealth inherited by multiple heirs who
        jointly own and manage the inheritance according to agreed terms.
        Similarly, partnership can arise from the shared benefit of an item,
        such as two individuals being gifted a book. In this case, the partners
        mutually decide how to use or divide the benefit, embodying the
        principles of shared ownership, cooperation, and fairness. This
        flexibility highlights the inclusive nature of Sharikah, accommodating
        diverse forms of partnerships in line with Islamic values.
        <br />
        Any one of the partners can annul the partnership without the consent of
        the other partners, as long as it does not violate a pre-stipulated
        agreement between them.
        <br />
        <br />
        <b>Types of Partnerships and thier conditions:</b> <br />
        <br />
        <b>Sharikat-ul-Inan (Partnership of Contribution)</b>
        <br />
        <br /> Sharikat-ul-Inan, or the "Partnership of Contribution," is a form
        of partnership in Islamic finance where all partners contribute both
        capital and effort whether physical or intellectual towards the shared
        enterprise. For instance, if two individuals start an electronics store,
        one partner might contribute 200 computers valued at 2 BTC, while the
        other provides 200 smartwatches valued at 1 BTC. The combined capital
        investment would then be calculated in monetary terms, establishing a
        capital ratio of 2/3 and 1/3 for each partner.
        <br />
        Certain conditions govern this type of partnership to ensure fairness
        and clarity:
        <br /> Capital Valuation: The capital invested must be expressed in a
        known currency or valued in terms of currency. <br />
        Authorized Contribution: Partners must contribute either their own
        capital or funds entrusted to them by a third party with explicit
        consent. The same applies to their physical and intellectual efforts.{" "}
        <br />
        Transparency in Investment: The capital contributed by each partner must
        be clearly defined and free from ambiguity. <br />
        Profit Sharing: Profits must be distributed based on a predetermined and
        agreed-upon percentage applicable to all transactions. It is
        impermissible to allocate profits in ways that divide time, goods, or
        trips; for instance, one partner taking profits in certain months or
        from specific products while the other claims profits from other deals.
        This structure emphasizes equity and clarity, ensuring mutual benefit
        and accountability within the partnership.
        <br />
        <br />
        <b>Al-Musaqah (Partnership in Cultivation)</b>
        <br />
        <br />
        Al-Musaqah, or the "Partnership in Cultivation," is a form of
        partnership in Islamic finance where the owner of land with
        fruit-bearing trees, such as date palms or grapevines, entrusts another
        person to water and care for the trees. In return, the caretaker is
        entitled to a share of the produce. However, this arrangement is
        governed by specific conditions to maintain fairness and prevent
        disputes. The share of the produce must be predetermined and clearly
        defined. For instance, it is not permissible for the landowner to
        stipulate that the caretaker will take the produce from specific months,
        certain parts of the land, particular trees, or fixed quantities such as
        100 kg, with the remainder going to the owner. Such arrangements lead to
        ambiguity, which can result in one party being unjustly harmed or
        benefited. The agreement must ensure that both parties share in all the
        produce according to a known percentage, fostering equity and trust.
        <br />
        <br />
        <b>Muzara’ah (Partnership in Agriculture)</b>
        <br />
        <br />
        Muzara’ah, or the "Partnership in Agriculture," is an arrangement in
        Islamic finance where the owner of agricultural land allows another
        person to cultivate crops on it. In exchange for their efforts, the
        cultivator is entitled to a predetermined share of the harvest. Like
        Musaqah, this form of partnership is subject to strict conditions to
        ensure fairness and prevent disputes. The share of the produce must be
        explicitly agreed upon and expressed as a known percentage. It is
        impermissible to allocate shares ambiguously, such as assigning certain
        months' produce, specific portions of the land, or fixed quantities like
        500 kg to one party while leaving the rest to the other. Such conditions
        could lead to unjust outcomes, benefiting one party at the expense of
        the other. Muzara’ah emphasizes equitable sharing, where both the
        landowner and the cultivator mutually benefit from the partnership.
        <br />
        <br />
        <b>Mudarabah (Partnership in Investment)</b>
        <br />
        <br />
        Mudarabah, or "Partnership in Investment," is a unique form of Islamic
        partnership where one party provides the capital, and the other party
        contributes their skills, labor, or expertise to manage and grow the
        investment. The profits generated are shared between the two parties
        based on a mutually agreed-upon percentage, which can vary according to
        their arrangement. However, in the event of a loss, the burden falls
        entirely on the investor, as the working partner (the Mudarib) is not
        liable for the capital unless negligence or misconduct is proven. Like
        other forms of partnership in Islamic finance, Mudarabah is governed by
        essential conditions, including transparency in profit-sharing ratios,
        clarity in the investment capital, and mutual consent to ensure
        fairness. This structure allows for productive collaboration between
        capital owners and skilled individuals while adhering to Islamic
        principles of equity and justice.
        <br />
        <br />
        <b>Principle of Loss and Profit Sharing in Partnership</b>
        <br />
        <br />A fundamental principle in Islamic partnerships is that losses
        must be borne in proportion to the capital invested by each partner,
        while the profit-sharing ratio can be determined through mutual
        agreement. For example, if two partners each contribute 50% of the
        capital, they can agree that one partner will receive 60% of the profits
        and the other 40%. However, in the event of a loss, both partners must
        bear the loss equally, in line with their respective 50% contributions.
        This principle ensures fairness and aligns with Islamic guidelines,
        preventing exploitation while promoting transparency and mutual consent
        in financial dealings.
        <br />
        <br />
        <b>Flexibility in Managing Partnership Ventures</b>
        <br />
        <br />
        According to the majority of Islamic scholars, it is not a requirement
        for partners to merge their inventory or sell their goods in the same
        location. For instance, if two partners contribute different items, such
        as computers and smartwatches, they can still maintain a valid
        partnership even if one sells computers in one shop and the other sells
        smartwatches in another. The key condition is that both partners agree
        on a fixed and known proportion for sharing profits and losses. This
        flexibility allows for diverse business arrangements while adhering to
        the principles of fairness and mutual consent in partnerships.
        <br />
        <br />
        <b>Diversity in Capital Contributions</b>
        <br />
        <br />
        It is not a condition for partners in an Islamic partnership to invest
        capital of the same species or type. Partners can contribute different
        types of goods and still form a valid partnership. For example, one
        partner may invest in livestock while the other invests in agricultural
        equipment. As long as the value of their contributions is agreed upon,
        and profits and losses are shared according to a known and fair
        proportion, the partnership remains valid. This principle ensures
        inclusivity and adaptability in business arrangements.
        <br />
        <br />
        <b>Summary of Today's Lesson</b>
        <br />
        <br />
        Today’s lesson focused on the Islamic concept of Sharikah (partnership),
        which allows two or more parties to collaborate in business by sharing
        capital, effort, and profit according to agreed-upon terms. We explored
        how Islamic partnerships are based on principles of trust, transparency,
        and mutual consent, with a strong emphasis on fairness in profit-sharing
        and loss-bearing. The session covered various types of partnerships
        including Sharikat-ul-Inan (contribution-based), Mudarabah
        (investment-based), Musaqah (cultivation-based), and Muzara’ah
        (agriculture-based), each with its own rules and conditions. We also
        learned that profit shares can be flexible if agreed upon in advance,
        but losses must always be borne in proportion to capital. By aligning
        business conduct with ethical and spiritual values, Islamic partnerships
        promote justice, reduce exploitation, and encourage economic
        cooperation.
        <br />
        <br />
        <b>Coming Up Next</b>
        <br />
        <br />
        As we continue our exploration of Islamic partnerships, Day 19 will
        delve deeper into the finer rulings and applications of Sharikah,
        focusing on scenarios involving unequal capital, different types of
        contributions, and partnerships that span across separate business
        locations or industries. We will also examine how partnerships can be
        dissolved, the role of trust and transparency in maintaining their
        validity, and how modern business practices can remain in harmony with
        classical Shariah principles. Prepare to gain a richer understanding of
        how Islamic law accommodates diverse, real-world business collaborations
        with justice and equity at its core.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

       <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default IslamicTradePage18;
